import { z } from 'zod';

import { BlockReasonSchema } from '../task/block_reason';
import { HandoffReasonSchema } from '../handoff_event/handoff_event';
import { TaskIdSchema } from '../task/task_id';
import { ActorSchema } from '../task/task';
import { ProjectIdSchema, SquadIdSchema } from '../project/project';
import { AppUserIdSchema } from '../user/user';

import { CardExternalRefSchema } from './card';
import { ClosureOutcomeSchema } from './closure';
import { HandoffTopicSchema } from './handoff_topic';
import { PrioritySchema } from './priority';

/**
 * Phase 5 — REST event envelope.
 *
 * One `POST /orgs/:orgId/cards/:cardId/events` route accepts a
 * {@link CardEventSchema} body and dispatches to the matching Phase 3
 * use case. The discriminator is `event.type`; per-type fields are
 * validated against the same Zod schemas the use cases consume.
 *
 * `correlationId` is optional — the dispatcher generates a fresh UUID
 * when missing so every audit row from a single request shares one.
 */
export const CardEventSchema = z
  .object({
    correlationId: z.uuid().optional(),
    event: z.discriminatedUnion('type', [
      z
        .object({
          type: z.literal('card.close'),
          outcome: ClosureOutcomeSchema,
          reason: z.string().max(2000).optional(),
          // When `true`, the close first drives every non-terminal task to
          // `done` instead of rejecting. Opt-in for system-driven closes
          // (e.g. a system closing a linked card whose AI task is still
          // `blocked`). Absent/false keeps
          // the default contract: a card with pending/running/blocked tasks
          // cannot be closed — the operator must finish them first.
          cascadeTasks: z.boolean().optional(),
        })
        .strict(),
      z.object({ type: z.literal('card.reopen') }).strict(),
      // Assume the card: assigns it to the requesting agent (server-side
      // actor; no client-supplied assignee). Mirrors `task.claim`.
      z.object({ type: z.literal('card.claim') }).strict(),
      // Manually forward a squad-less (triage) card to a squad. `projectId`
      // is the squad's parent project; both come from the client (the triage
      // UI resolves them from the squad picker).
      z
        .object({
          type: z.literal('card.assign-squad'),
          squadId: SquadIdSchema,
          projectId: ProjectIdSchema,
        })
        .strict(),
      // Transfer the card to another human attendant by their app user id.
      // The picker lists project members (userId-keyed); the server resolves
      // the userId → human agent (provisioning one if absent) before
      // assigning, so a member who never acted is still assignable.
      z.object({ type: z.literal('card.transfer-agent'), userId: AppUserIdSchema }).strict(),
      // Move the card to a different squad. `projectId` is the target
      // squad's parent project. Clears the current assignee server-side.
      z
        .object({
          type: z.literal('card.transfer-squad'),
          squadId: SquadIdSchema,
          projectId: ProjectIdSchema,
        })
        .strict(),
      // Strip the card's squad/project/assignee and re-route it through
      // automatic triage.
      z.object({ type: z.literal('card.return-to-triage') }).strict(),
      // Hand the card back to the AI: clears the human assignee (squad stays)
      // and publishes `card.assignment-changed` with `assigneeKind: 'agent'`
      // so the conversation service un-mutes the agent.
      z.object({ type: z.literal('card.return-to-agent') }).strict(),
      z
        .object({
          type: z.literal('card.update-meta'),
          subject: z.string().max(200).nullish(),
          summary: z.string().max(2000).nullish(),
          priority: PrioritySchema.optional(),
          handoffTopic: HandoffTopicSchema.optional(),
        })
        .strict()
        .refine(
          (v) =>
            v.subject !== undefined ||
            v.summary !== undefined ||
            v.priority !== undefined ||
            v.handoffTopic !== undefined,
          { message: 'at least one of subject/summary/priority/handoffTopic must be provided' },
        ),
      // Link an external-system reference to the card. Idempotent on
      // (system, externalId) — re-linking the same ref refreshes its snapshot
      // instead of duplicating.
      z
        .object({
          type: z.literal('card.link-external-ref'),
          externalRef: CardExternalRefSchema,
        })
        .strict(),
      z
        .object({
          type: z.literal('task.create'),
          description: z.string().min(1).max(2000),
          assigneeAgent: ActorSchema.optional(),
          priority: PrioritySchema.optional(),
          start: z.boolean().optional(),
        })
        .strict(),
      z
        .object({
          type: z.literal('task.claim'),
          taskId: TaskIdSchema,
          newAssigneeAgent: ActorSchema.optional(),
          reason: HandoffReasonSchema,
          category: z.string().max(50).optional(),
          note: z.string().max(5000).optional(),
        })
        .strict(),
      z.object({ type: z.literal('task.start'), taskId: TaskIdSchema }).strict(),
      z
        .object({
          type: z.literal('task.block'),
          taskId: TaskIdSchema,
          blockReason: BlockReasonSchema,
        })
        .strict(),
      z.object({ type: z.literal('task.unblock'), taskId: TaskIdSchema }).strict(),
      z.object({ type: z.literal('task.complete'), taskId: TaskIdSchema }).strict(),
    ]),
  })
  .strict();

export type CardEvent = z.infer<typeof CardEventSchema>;
